// client/src/pages/CalendarPage.js
// Page for managing and viewing school events and calendar

import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, IconButton, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputLabel, Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../utils/fetchJson';
import { useSnackbar } from 'notistack';

export default function CalendarPage() {
  // State declarations
  // --- CRUD for Events/Calendar ---
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', type: '', startDate: '', endDate: '', evaluationTypeId: '', description: '' });

  // --- Premium Features: Bulk Actions, Import/Export, Advanced Filtering, Audit Log, Real-time ---
  const [selectedRows, setSelectedRows] = useState([]);

  const [evaluationTypes, setEvaluationTypes] = useState([]);
  const [evaluationTypesLoading, setEvaluationTypesLoading] = useState(false);

  const resolveEvaluationTypeName = (evaluationTypeId) => {
    const id = String(evaluationTypeId || '').trim();
    if (!id) return '';
    const row = (evaluationTypes || []).find((x) => String(x?.id) === id) || null;
    return String(row?.name || '').trim();
  };

  const resolveEventStartDate = (row) => String(row?.startDate || row?.date || '').trim();
  const resolveEventEndDate = (row) => String(row?.endDate || row?.startDate || row?.date || '').trim();
  const formatDateRange = (start, end) => {
    const s = String(start || '').trim();
    const e = String(end || '').trim();
    if (!s && !e) return '';
    if (!e || s === e) return s || e;
    return `${s} → ${e}`;
  };

  const loadEvaluationTypes = useCallback(async () => {
    setEvaluationTypesLoading(true);
    try {
      const data = await fetchJson('/api/evaluation-types');
      const list = Array.isArray(data) ? data : (data?.data || data?.types || []);
      setEvaluationTypes(Array.isArray(list) ? list : []);
    } catch (_) {
      setEvaluationTypes([]);
    }
    setEvaluationTypesLoading(false);
  }, []);

  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const apiBase = (() => {
    const configuredBaseRaw = (
      process.env.REACT_APP_API_BASE_URL ||
      process.env.VITE_API_BASE_URL ||
      process.env.NEXT_PUBLIC_API_BASE_URL ||
      ''
    ).trim();
    const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocalBrowserHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(browserHost);
    const isLocalNodeEnv = process.env.NODE_ENV === 'development';
    const baseRaw = configuredBaseRaw || ((isLocalBrowserHost || isLocalNodeEnv) ? 'http://localhost:5000' : '');
    return (baseRaw || '').replace(/\/$/, '');
  })();

  const buildScopedAuthHeaders = () => {
    const tokenKeys = ['impersonationToken', 'accessToken', 'token', 'authToken', 'jwt', 'id_token', 'access_token', 'ACCESS_TOKEN'];
    let token = '';
    for (const k of tokenKeys) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k);
      if (v) {
        token = v;
        break;
      }
    }

    try {
      token = String(token || '').trim();
      if (token.toLowerCase().startsWith('bearer ')) {
        token = token.slice(7).trim();
      }
      token = token.replace(/^"+|"+$/g, '').replace(/^'+|'+$/g, '');
    } catch (_) {
      token = '';
    }

    const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
    const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
    const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
    const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
    const ayId = localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '';

    const headers = {
      ...(schoolId ? { 'X-Scope-School-Id': schoolId, 'X-School-Id': schoolId } : {}),
      ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {}),
      ...(phaseId ? { 'X-Active-Phase-Id': phaseId } : {}),
      ...(ayId ? { 'X-Academic-Year-Id': ayId } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };

    return headers;
  };

  const fetchEvents = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchJson('/api/calendar-events');
      setEvents(data?.events || []);
    } catch (e) {
      setError(t('calendarPage.errors.loadEvents') || 'Failed to load events');
    }
    setLoading(false);
  };


  useEffect(() => {
    fetchEvents();
    loadEvaluationTypes();
  }, [loadEvaluationTypes]);

  useEffect(() => {
    const handler = () => loadEvaluationTypes();
    window.addEventListener('app:phase:changed', handler);
    window.addEventListener('app:ay:changed', handler);
    return () => {
      window.removeEventListener('app:phase:changed', handler);
      window.removeEventListener('app:ay:changed', handler);
    };
  }, [loadEvaluationTypes]);

  const handleEdit = (row) => {
    setEditingEvent(row);
    const start = resolveEventStartDate(row);
    const end = resolveEventEndDate(row);
    setEditForm({
      title: row.title,
      type: row.type,
      startDate: start,
      endDate: end,
      evaluationTypeId: row?.evaluationTypeId != null ? String(row.evaluationTypeId) : '',
      description: row.description || ''
    });
    setEditDialogOpen(true);
  };
  const handleEditFormChange = (field, value) => {
    setEditForm(f => ({ ...f, [field]: value }));
  };
  const handleEditSubmit = async () => {
    setLoading(true);
    try {
      if (editingEvent) {
        await fetchJson(`/api/calendar-events/${editingEvent.id}`, {
          method: 'PUT',
          body: { ...editForm, date: editForm.startDate }
        });
      } else {
        await fetchJson('/api/calendar-events', {
          method: 'POST',
          body: { ...editForm, date: editForm.startDate }
        });
      }
      enqueueSnackbar(t('calendarPage.success.saveEvent') || 'Saved successfully', { variant: 'success' });
      setEditDialogOpen(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (e) {
      setError(t('calendarPage.errors.saveEvent') || 'Failed to save event');
    }
    setLoading(false);
  };
  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await fetchJson(`/api/calendar-events/${id}`, { method: 'DELETE' });
      fetchEvents();
    } catch (e) {
      setError(t('calendarPage.errors.deleteEvent') || 'Failed to delete event');
    }
    setLoading(false);
  };
  const handleAdd = () => {
    setEditingEvent(null);
    setEditForm({ title: '', type: '', startDate: '', endDate: '', evaluationTypeId: '', description: '' });
    setEditDialogOpen(true);
  };
  // The filter, import and export controls were removed: a school plans a term
  // by reading it, and three controls guarding a list this size cost more
  // attention than they returned. The grid still sorts on every column.
  const filteredEvents = events;

  const handleBulkDelete = async () => {
    setLoading(true);
    try {
      await fetchJson('/api/calendar-events/bulk-delete', {
        method: 'POST',
        body: { ids: selectedRows }
      });
      setSelectedRows([]);
      fetchEvents();
    } catch (e) {
      setError(t('calendarPage.errors.bulkDelete') || 'Failed to delete events');
    }
    setLoading(false);
  };

  // DataGrid columns definition for events
  // Order follows how a school reads an event: what it is, what kind, which
  // evaluation it drives, the detail, then when. The ID column was dropped —
  // a database key is not information a head needs while planning a term.
  const columns = [
    { field: 'title', headerName: t('calendarPage.titleCol') || 'Title', flex: 1, minWidth: 160 },
    { field: 'type', headerName: t('calendarPage.typeCol') || 'Type', width: 120 },
    {
      field: 'evaluationTypeId',
      headerName: t('calendarPage.evaluationCol') || 'Evaluation',
      width: 180,
      valueGetter: (params) => resolveEvaluationTypeName(params.row?.evaluationTypeId)
    },
    { field: 'description', headerName: t('calendarPage.descriptionCol') || 'Description', flex: 1, minWidth: 200 },
    {
      field: 'startDate',
      headerName: t('calendarPage.startDateCol') || 'Start Date',
      width: 120,
      valueGetter: (params) => resolveEventStartDate(params.row)
    },
    {
      field: 'endDate',
      headerName: t('calendarPage.endDateCol') || 'End Date',
      width: 120,
      valueGetter: (params) => resolveEventEndDate(params.row)
    },
    {
      field: 'actions',
      headerName: t('calendarPage.actions') || 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={() => handleEdit(params.row)} aria-label={t('calendarPage.editEvent') || 'Edit event'}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)} aria-label={t('calendarPage.deleteEvent') || 'Delete event'}><DeleteIcon fontSize="small" /></IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ background: theme.background, color: theme.text, minHeight: '100vh', p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom>
        {t('calendarPage.title') || 'School Calendar & Events'}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {/* A labelled button, not a bare "+". The primary action on this page
            deserves to name itself rather than be guessed at from an icon. */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          aria-label={t('calendarPage.addTitle') || 'Add Event'}
        >
          {t('calendarPage.addTitle') || 'Add Event'}
        </Button>
      </Box>
      <Box sx={{ mt: 3, width: '100%', height: 500 }}>
        <DataGrid
          rows={filteredEvents}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          checkboxSelection
          disableRowSelectionOnClick
          selectionModel={selectedRows}
          onRowSelectionModelChange={(ids) => setSelectedRows(Array.isArray(ids) ? ids : Array.from(ids))}
          loading={loading}
          getRowId={(row) => row.id}
        />
      </Box>
      {selectedRows.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button variant="outlined" color="error" onClick={handleBulkDelete}>{t('calendarPage.bulkDelete') || 'Delete Selected'}</Button>
          {/* Add more bulk actions here if needed */}
        </Box>
      )}
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm" aria-labelledby="edit-event-dialog-title">
        <DialogTitle id="edit-event-dialog-title">{editingEvent ? (t('calendarPage.editTitle') || 'Edit Event') : (t('calendarPage.addTitle') || 'Add Event')}</DialogTitle>
        <DialogContent>
          <TextField label={t('calendarPage.titleCol') || 'Title'} value={editForm.title} onChange={e => handleEditFormChange('title', e.target.value)} fullWidth sx={{ mb: 2 }} required inputProps={{ 'aria-label': t('calendarPage.titleCol') || 'Title' }} helperText={t('calendarPage.titleRequired') || 'Title is required'} />
          <InputLabel>{t('calendarPage.typeCol') || 'Type'}</InputLabel>
          <Select
            value={editForm.type}
            onChange={e => {
              const nextType = e.target.value;
              setEditForm(f => ({
                ...f,
                type: nextType,
                ...(String(nextType || '').toLowerCase() === 'exam' ? {} : { evaluationTypeId: '' })
              }));
            }}
            fullWidth
            sx={{ mb: 2 }}
            required
            inputProps={{ 'aria-label': t('calendarPage.typeCol') || 'Type' }}
          >
            <MenuItem value="Holiday">{t('calendarPage.typeHoliday') || 'Holiday'}</MenuItem>
            {/* Displayed as "Evaluation"; the stored value stays 'Exam' so
                events already saved, and the branch below that keys on it,
                keep working. Renaming the value would orphan every existing
                exam event. */}
            <MenuItem value="Exam">{t('calendarPage.typeEvaluation') || 'Evaluation'}</MenuItem>
            <MenuItem value="Meeting">{t('calendarPage.typeMeeting') || 'Meeting'}</MenuItem>
            <MenuItem value="Other">{t('calendarPage.typeOther') || 'Other'}</MenuItem>
          </Select>

          {String(editForm.type || '').toLowerCase() === 'exam' && (
            <>
              <InputLabel>{t('calendarPage.evaluationCol') || 'Evaluation'}</InputLabel>
              <Select
                value={editForm.evaluationTypeId || ''}
                onChange={(e) => {
                  const id = String(e.target.value || '');
                  const selected = (evaluationTypes || []).find((x) => String(x?.id) === id) || null;
                  setEditForm((f) => ({
                    ...f,
                    evaluationTypeId: id,
                    ...(f.title ? {} : { title: String(selected?.name || '').trim() })
                  }));
                }}
                fullWidth
                sx={{ mb: 2 }}
                disabled={evaluationTypesLoading}
                displayEmpty
              >
                <MenuItem value="">{t('common.select') || 'Select'}</MenuItem>
                {(evaluationTypes || []).map((et) => (
                  <MenuItem key={et.id} value={String(et.id)}>
                    {String(et?.name || '').trim() || `Evaluation ${et.id}`}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}

          <TextField label={t('calendarPage.startDateCol') || 'Start Date'} type="date" value={editForm.startDate} onChange={e => handleEditFormChange('startDate', e.target.value)} fullWidth sx={{ mb: 2 }} InputLabelProps={{ shrink: true }} required inputProps={{ 'aria-label': t('calendarPage.startDateCol') || 'Start Date' }} />
          <TextField label={t('calendarPage.endDateCol') || 'End Date'} type="date" value={editForm.endDate} onChange={e => handleEditFormChange('endDate', e.target.value)} fullWidth sx={{ mb: 2 }} InputLabelProps={{ shrink: true }} required inputProps={{ 'aria-label': t('calendarPage.endDateCol') || 'End Date' }} />
          <TextField label={t('calendarPage.descriptionCol') || 'Description'} value={editForm.description} onChange={e => handleEditFormChange('description', e.target.value)} fullWidth multiline rows={3} inputProps={{ 'aria-label': t('calendarPage.descriptionCol') || 'Description' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel') || 'Cancel'}</Button>
          <Button onClick={handleEditSubmit} variant="contained">{t('common.save') || 'Save'}</Button>
        </DialogActions>
      </Dialog>
      {/* Import Dialog */}
      {/* Audit Log Dialog */}
    </Box>
  );
}
